// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { getPricingOptionLabel } from '../../util/pricingOptions';
import { framedTransformOrigin } from '../../util/adFraming';
import { cardFraming, cardFramingVariant } from '../../util/cardFraming';
import {
  readDecodedImageSize,
  setDecodedImageSizeFromElement,
} from '../../util/useDecodedImageSize';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';
import useFavorites from '../../hooks/useFavorites';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/* ─── Icons ──────────────────────────────────────────────────── */
const HeartIcon = ({ filled }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

/* ─── Helpers ────────────────────────────────────────────────── */
// Story 12.8 — pricingOption labels now come from the shared map in
// src/util/pricingOptions.js (was a drifted local PRICE_OPTION_LABELS).

const formatLabel = s => (s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null);

// on-hold: seller has temporarily paused the listing (publicData.listingStatus === 'on_hold').
// Inquiries remain active — backup buyers are valuable to sellers.
// Hold reasons: pending_sale → "Pending Sale", injury → "Recovering from Injury",
//   updating → "Updating the Listing". Missing/unrecognized holdReason shows "On Hold".
// 60-day auto-close rule is enforced in tph-api (not in this build).
// On-hold listings appear in search but NOT in Related Horses.
const HOLD_REASON_LABELS = {
  pending_sale: 'Pending Sale',
  injury: 'Recovering from Injury',
  updating: 'Updating the Listing',
};

/* ─── Image sub-component (kept for lazy loading + ResponsiveImage) ── */
/**
 * 🔴 Story 12.15 — THE CARD HONOURS THE SELLER'S FRAMING.
 *
 * Before this story the card rendered the `listing-card*` derivatives, which are
 * `fit: 'crop'` + imgix `crop=edges` — an algorithm's guess at the interesting
 * region, applied server-side. A focal point cannot be honoured against those
 * pixels because they are already gone, and `object-position` alone would carry
 * pan but not zoom.
 *
 * So the card now resolves an ASPECT-PRESERVING variant and frames it with
 * `framedImageStyle`, the same helper the listing-page hero and the wizard tiles
 * use. `util/cardFraming.js` owns both decisions; this component owns none of
 * them.
 *
 * ⚠️ WE ARE NOT "FIXING A CENTRE CROP". Every `listing-card` URL Sharetribe
 * returns carries `crop=edges` (verified 90/90 live). This replaces an
 * algorithm's guess with the seller's intent — a real win and a smaller
 * subjective delta than the arithmetic suggests.
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    aspectWidth,
    aspectHeight,
    decodedSize,
    onImageDecoded,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const framingUrl = props.framingUrl;

  // 🔴 AC-5(c) — the SINGLE-CARD surfaces (`PostPublishScreen`, the map info
  // card, the Step-3 preview) resolve the decode locally rather than being
  // handed a container map. This state exists only to re-render the one card
  // that just decoded; the value itself is read back out of the shared module
  // cache, which `handleLoad` writes first.
  const [localDecoded, setLocalDecoded] = useState(null);

  // 🔴 AC-5(d) — the module cache is SHARED with the gallery hero and the wizard
  // tiles, so a photo one of them already decoded resolves SYNCHRONOUSLY here
  // with no flicker back through the un-decoded fallback. On the wizard that is
  // the common case: the Step-3 card preview and the tiles both resolve
  // `scaled-xlarge`.
  const resolvedDecoded =
    decodedSize ||
    readDecodedImageSize(framingUrl) ||
    (localDecoded?.url === framingUrl ? localDecoded.size : null);

  const { variantNames, style: framedStyle } = cardFraming({
    image: firstImage,
    focalPoints: listing?.attributes?.publicData?.photoFocalPoints,
    targetWidth: aspectWidth,
    targetHeight: aspectHeight,
    decodedSize: resolvedDecoded,
  });

  const isFramed = !!framedStyle;

  // 🔴 AC-7 — honest degradation. Without an aspect-preserving variant the card
  // renders the cropped derivative UNFRAMED: today's plain `object-fit: cover`,
  // exactly as now. Never blank, never "No image". This is the normal state for
  // a just-uploaded cover and resolves itself when the derivative lands.
  const variants = isFramed
    ? variantNames
    : firstImage?.attributes?.variants
    ? Object.keys(firstImage.attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // 🔴 AC-6 — keep the box centre fixed under the 1.03 hover scale, or a photo
  // framed toward the top slides down on every mouseover.
  const transformOrigin = framedTransformOrigin(framedStyle);

  // 🔴 AC-5(b′) — the decode is read off the RENDERED `<img>`, never from an
  // off-DOM probe: zero extra bytes at any DPR, and nothing pulled ahead of the
  // card lazy-loader. `ResponsiveImage` spreads `...rest` onto the `<img>`, so
  // `onLoad` passes through with no component change — the same seam `style`
  // uses.
  const handleLoad = event => {
    const element = event?.currentTarget;
    if (!framingUrl || !element) {
      return;
    }
    const next = setDecodedImageSizeFromElement(framingUrl, element);
    if (!next) {
      return;
    }
    if (onImageDecoded) {
      // List surface: the container owns the map (AC-5c).
      onImageDecoded(framingUrl, next);
    } else {
      // Single-card surface: re-render just this card.
      setLocalDecoded(previous =>
        previous?.url === framingUrl &&
        previous.size.width === next.width &&
        previous.size.height === next.height
          ? previous
          : { url: framingUrl, size: next }
      );
    }
  };

  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    // 🔴 AC-4(c) — the box aspect and the framing target come from the SAME two
    // config values, so they can never disagree. No destructuring defaults: if
    // Console ever moves the listing aspect off 4:3, a config shape missing
    // these keys must fail loudly in `assertTarget` rather than silently frame
    // against a stale hardcoded 4:3.
    <AspectRatioWrapper
      className={aspectRatioClassName || css.cardPhotoAspect}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={isFramed ? classNames(css.cardImg, css.cardImgFramed) : css.cardImg}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
        {...(isFramed ? { style: { ...framedStyle, transformOrigin } } : {})}
        onLoad={handleLoad}
      />
    </AspectRatioWrapper>
  );
};

/**
 * The container of the card's photo face.
 *
 * DEFAULT — every buyer-facing surface, all eleven of them — is the
 * `<NamedLink>` to the listing page, byte-for-byte the element that has always
 * been there. The props, the class and the `ariaLabel` expression are unchanged;
 * only the JSX tag that spells it moved.
 *
 * 🔴 PREVIEW ONLY it becomes a plain `<div>`. This is not cosmetic. A
 * pointer-down inside an `<a href>` starts the browser's own native link drag,
 * and that drag PRE-EMPTS any drag the page wanted to run — the identical
 * failure class already written up at `EditListingPhotosForm.js:426-437`
 * ("Chrome and WebKit refuse to begin a drag of a `draggable` ANCESTOR when the
 * mousedown lands on a form control"). With the anchor in place a seller could
 * not drag to frame their photo at all. It also happens to be the right verb:
 * the seller is looking at their OWN listing inside the wizard and must not be
 * navigated out of it.
 */
const CardPhotoFrame = props => {
  const { previewMode, hasSlot, id, slug, title, isSold, children, linkState } = props;
  return previewMode ? (
    <div
      className={classNames(css.cardPhoto, { [css.cardPhotoLive]: hasSlot })}
      data-testid="card-photo-preview"
    >
      {children}
    </div>
  ) : (
    <NamedLink
      name="ListingPage"
      params={{ id, slug }}
      to={linkState ? { state: linkState } : undefined}
      className={css.cardPhoto}
      ariaLabel={isSold ? `${title} — This listing is sold` : title}
    >
      {children}
    </NamedLink>
  );
};

/**
 * ListingCard — styled to match the Related Horses HorseCard design.
 * Sold and on-hold treatments layer on top of this base design.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const { isFavorited, toggleFavorite } = useFavorites();
  const [heartMsg, setHeartMsg] = useState(null);
  const heartTimer = useRef(null);

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    listing,
    renderSizes,
    setActiveListing,
    lazyLoadImage = true,
    onCardClick,
    isViewed = false,
    // 🔴 Additive, default OFF — no existing caller changes behaviour.
    //
    // For SELLER-FACING PREVIEW contexts: rendering the card for the seller's
    // own listing inside an `inert` wrapper, where a "Save to favorites" control
    // is the wrong verb for the wrong person and taps do nothing anyway.
    // `previewMode` drops the heart and nothing else — everything else the card
    // shows is true of the listing, and hiding truth would make a preview a lie.
    //
    // Consumed by `EditListingPhotosForm`'s `CardPreview` (Step 3). Widened
    // from "drops the heart" to "the whole card is dead, except the photo":
    //   · the photo's `<NamedLink>` becomes a plain `<div>`   (`CardPhotoFrame`)
    //   · the heart is dropped                                (below)
    //   · the horse name renders as a `<span>`, and its stretched-link
    //     `::after` is switched off                           (below)
    //   · `.cardBody` is marked `inert`                       (below)
    //   · `onCardClick` is not wired                          (below)
    // Nothing was subtracted from what it already showed: everything the card
    // displays is still true of the listing, and hiding truth would make a
    // preview a lie.
    previewMode = false,
    // 🔴 Additive, default OFF, and READ ONLY UNDER `previewMode` — a
    // buyer-facing surface that passed this by accident would still render the
    // shipped card. It replaces the card's `<img>` with a caller-supplied live
    // surface (Step 3 hands it a `<PhotoFramer>`), so the seller frames the
    // photo in the very card the buyer will see, rather than in a second 4:3
    // box beside it. The chrome that floats over the photo — hold band, price
    // chip, signal badge, sold overlay — is unchanged and still paints on top.
    photoSlot = null,
    // Optional. Fired after the heart toggles with { id, title, wasAdded }.
    // Backward-compatible — existing callers (HomeListings, SearchResultsPanel)
    // don't pass it. The Favorites page uses it to drive its undo toast.
    onFavoriteToggle,
    // Optional Phase-2b favorites signals. Backward-compatible — only the
    // Favorites page passes these; everywhere else they're undefined and render
    // nothing. `signalBadge` = { icon, label } photo badge (e.g. ↓ Price reduced);
    // `wasPriceLabel` = pre-drop price string shown struck-through by the chip.
    signalBadge,
    wasPriceLabel,
    // 🔴 Story 12.15 (AC-5c) — ONE DECODED MAP PER SURFACE, NEVER A HOOK PER
    // CARD. A hook inside a `.map()` over 24 search results is a rules-of-hooks
    // violation the moment the result count changes, so the list containers own
    // the map and hand each card its entry. `onImageDecoded(url, size)` is the
    // container's single functional-`setState` updater, called from this card's
    // own `<img>` `onLoad`. Both optional: a single-card surface may omit them
    // and let the shared module cache do the work.
    decodedSize,
    onImageDecoded,
    // Story 7.19 (AC9) — which on-site surface this card is rendered on
    // ('search' | 'homepage' | 'favorites' | 'seller_profile'), threaded
    // through both NamedLinks' `state` so the listing page can read
    // `location.state.sourcePage` and record it on the view. Optional —
    // omitted entirely (never sent as e.g. '' or null) when a caller doesn't
    // pass one, same as every other on-site navigation with no hint.
    sourcePage,
  } = props;
  const listingPageLinkState = sourcePage ? { sourcePage } : undefined;

  const { title = '', price, publicData } = listing?.attributes || {};
  const slug = createSlug(title);
  const id = listing?.id?.uuid;

  const {
    listingType,
    cardStyle,
    pricingOption,
    birthYear,
    color,
    sex,
    sire,
    dam,
    damsSire,
    disciplines,
    listingStatus,
    holdReason,
  } = publicData || {};

  const isSold = listingStatus === 'sold';

  // on-hold: seller has temporarily paused this listing
  const isOnHold = listingStatus === 'on_hold';

  // 🔴 Story 12.15 — the key a container's decoded map is keyed by, resolved
  // through the ONE shared helper. No caller re-derives the variant preference.
  const framingUrl = cardFramingVariant(listing?.images?.[0])?.url || null;

  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  // 🔴 Story 12.15 (AC-4c) — STOP HARDCODING 4/3, AND NO DESTRUCTURING
  // DEFAULTS. `aspectWidth` / `aspectHeight` feed BOTH the `AspectRatioWrapper`
  // box and `framedImageStyle`'s target, so the box and the framing can never
  // disagree.
  //
  // A `= 4` / `= 3` fallback here would quietly re-create the exact thing this
  // rule exists to forbid: a hardcoded target standing in for the config one. If
  // Console ever moves the listing aspect off 4:3, a config shape missing these
  // keys must fail loudly in `assertTarget` (which throws with the offending
  // value) rather than frame against a stale 4:3 for every visitor. `undefined`
  // is allowed to reach it on purpose.
  const { variantPrefix = 'listing-card', aspectWidth, aspectHeight } = config.layout.listingImage;

  // Price display — label options take precedence, then formatted price, then fallback
  const translations = getListingCardTranslations(listing, config, intl);
  const { priceMessage } = translations;
  const customPriceLabel =
    getPricingOptionLabel(pricingOption) || (!price ? 'Contact for Price' : null);
  const displayPriceLabel = customPriceLabel || priceMessage;

  // Fact pills — combine color + sex like Related Horses cards
  const colorSex = [color && formatLabel(color), sex && formatLabel(sex)]
    .filter(Boolean)
    .join(' · ');
  const primaryDiscipline = disciplines?.[0] ? formatLabel(disciplines[0]) : null;

  // Mouse handlers for search map pin highlighting
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // 🔴 THE SELECTIVE GUARD THAT REPLACED `CardPreview`'s BLANKET ONE.
  //
  // `CardPreview` used to set `inert` on the wrapper AROUND this whole card.
  // `inert` cascades over its entire subtree and cannot be lifted off an island
  // inside it, so the very attribute that made the preview safe is what made the
  // photo undraggable — the two could not coexist. Narrowing it to `.cardBody`
  // keeps the guarantee exactly where it was needed (name, pills, pedigree stay
  // dead to pointer, keyboard AND focus, so nothing in the body can be tabbed to
  // or clicked) while leaving the photo above it live.
  //
  // It lives HERE, not in the caller, for two reasons: the caller cannot reach
  // this node, and a body that later grows a link is then still covered without
  // anyone remembering to go and re-guard it.
  //
  // Set imperatively for the reason the original comment gave: React 18.3 does
  // not render `inert` from a boolean prop (that landed in React 19).
  const previewBodyRef = useRef(null);
  useEffect(() => {
    const node = previewBodyRef.current;
    if (previewMode && node) {
      node.setAttribute('inert', '');
    }
  }, [previewMode]);

  const handleHeartClick = e => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(id, wasAdded => {
      setHeartMsg(wasAdded ? 'added' : 'removed');
      clearTimeout(heartTimer.current);
      heartTimer.current = setTimeout(() => setHeartMsg(null), 2000);
      if (onFavoriteToggle) onFavoriteToggle({ id, title, wasAdded });
    });
  };

  return (
    <div
      className={classNames(css.card, rootClassName || className, {
        [css.cardPreviewRoot]: previewMode,
      })}
      {...setActivePropsMaybe}
      // Story 8.2 (AC9) click telemetry: clicks on the photo/title NamedLinks
      // bubble here (the heart button stopsPropagation). Fire-and-forget — does
      // not block navigation. No-op when onCardClick is not provided.
      // …and it is not wired at all in preview mode: the seller's own draft has
      // no click telemetry to fire and nothing to navigate to.
      onClick={!previewMode && onCardClick ? () => onCardClick(id) : undefined}
    >
      {/* Photo — price chip floating top-left, sold overlay on top */}
      <CardPhotoFrame
        previewMode={previewMode}
        hasSlot={!!photoSlot}
        id={id}
        slug={slug}
        title={title}
        isSold={isSold}
        linkState={listingPageLinkState}
      >
        {previewMode && photoSlot ? (
          <div className={css.cardPhotoSlot}>{photoSlot}</div>
        ) : showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={title}
            listing={listing}
            variantPrefix={variantPrefix}
            aspectRatioClassName={aspectRatioClassName || css.cardPhotoAspect}
            lazyLoadImage={lazyLoadImage}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            framingUrl={framingUrl}
            decodedSize={decodedSize}
            onImageDecoded={onImageDecoded}
          />
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={css.cardPhotoAspect}
            width={aspectWidth}
            height={aspectHeight}
          />
        )}
        {/* on-hold (B3): bottom gradient band over the photo. Card body stays
            untouched so on-hold cards keep the same height as active cards.
            Rendered before the price chip so the chip paints above it. */}
        {isOnHold && (
          <div
            className={css.holdBand}
            role="status"
            aria-label={`${HOLD_REASON_LABELS[holdReason] || 'On Hold'} — inquiries still received`}
          >
            <span className={css.holdBandLabel} aria-hidden="true">
              {HOLD_REASON_LABELS[holdReason] || 'On Hold'}
            </span>
            <span className={css.holdBandSub} aria-hidden="true">
              inquiries still received
            </span>
          </div>
        )}
        <div className={classNames(css.priceChip, { [css.priceChipSold]: isSold })}>
          {displayPriceLabel}
          {wasPriceLabel && <span className={css.priceWas}>{wasPriceLabel}</span>}
        </div>
        {/* Favorites change-signal badge (Phase 2b) — bottom-left over the photo.
            Only the Favorites page passes signalBadge, and only for available
            (non-sold, non-on-hold) cards, so it never collides with the sold
            overlay or the on-hold band. */}
        {signalBadge && !isSold && (
          <span className={css.photoBadge}>
            <span aria-hidden="true">{signalBadge.icon}</span>
            {signalBadge.label}
          </span>
        )}
        {/* Sold treatment: maroon gradient overlay + SOLD medallion */}
        {isSold && (
          <div className={css.soldOverlay} aria-label="This listing is sold">
            <div className={css.soldMedallion} aria-hidden="true">
              <span className={css.soldText}>SOLD</span>
              <span className={css.soldSub}>PREMIER HORSE</span>
            </div>
          </div>
        )}
      </CardPhotoFrame>

      {/* Heart button — top-right over photo, outside NamedLink to avoid nested interactives */}
      {previewMode ? null : (
        <div className={css.heartWrap}>
          <button
            className={classNames(css.heartBtn, { [css.heartBtnFavorited]: isFavorited(id) })}
            type="button"
            aria-label={isFavorited(id) ? 'Remove from favorites' : 'Save to favorites'}
            onClick={handleHeartClick}
          >
            <HeartIcon filled={isFavorited(id)} />
          </button>
          {heartMsg && (
            <div className={css.heartTooltip}>
              {heartMsg === 'added' && (
                <span className={css.viewedCheck} aria-hidden="true">
                  ✓
                </span>
              )}
              {heartMsg === 'added' ? 'Added to favorites!' : 'Removed from favorites'}
            </div>
          )}
        </div>
      )}

      {/* Card body — name, fact pills, pedigree table */}
      <div className={css.cardBody} ref={previewBodyRef}>
        {/* Name row — horse name left, device-local "Viewed" marker right.
            The row reserves the marker's space in both states so unseen and
            viewed cards have an identical name-row height (no layout shift). */}
        <div className={css.cardNameRow}>
          {/* 🔴 PREVIEW: a `<span>`, not a link, and `.cardNameStatic` switches
              off the `::after` — which is LOAD-BEARING, not tidiness. That
              pseudo-element is the card's stretched link
              (`ListingCard.module.css:240-247`): `position:absolute; inset:0`
              over the WHOLE card, photo included. Leave it painting and it
              blankets the framing surface and swallows every pointer-down,
              so the drag would die even with the photo's own anchor gone. */}
          {previewMode ? (
            <span
              className={classNames(css.cardName, css.cardNameStatic, {
                [css.cardNameSold]: isSold,
              })}
            >
              {title || '—'}
            </span>
          ) : (
            <NamedLink
              name="ListingPage"
              params={{ id, slug }}
              to={listingPageLinkState ? { state: listingPageLinkState } : undefined}
              className={classNames(css.cardName, { [css.cardNameSold]: isSold })}
            >
              {title || '—'}
            </NamedLink>
          )}
          {/* Viewed marker: sits above the .cardName::after stretched-link
              overlay (z-index > 1) so the label isn't swallowed by the card
              click target. The gold check is decorative; the word "Viewed"
              carries the accessible text (not aria-hidden). */}
          {isViewed && (
            <span className={css.viewedMarker}>
              <span className={css.viewedCheck} aria-hidden="true">
                ✓
              </span>
              Viewed
            </span>
          )}
        </div>

        <div className={css.factPills}>
          {birthYear && (
            <span className={classNames(css.factPill, { [css.factPillSold]: isSold })}>
              {birthYear}
            </span>
          )}
          {colorSex && (
            <span className={classNames(css.factPill, { [css.factPillSold]: isSold })}>
              {colorSex}
            </span>
          )}
          {primaryDiscipline && (
            <span className={classNames(css.factPill, { [css.factPillSold]: isSold })}>
              {primaryDiscipline}
            </span>
          )}
        </div>

        <div className={css.pedTable}>
          {[
            { label: 'Sire', value: sire },
            { label: 'Dam', value: dam },
            { label: "Dam's Sire", value: damsSire },
          ].map(({ label, value }) => (
            <div key={label} className={css.pedRow}>
              <span className={classNames(css.pedKey, { [css.pedKeySold]: isSold })}>{label}</span>
              <span className={classNames(css.pedVal, { [css.pedValSold]: isSold })}>
                {value || '—'}
              </span>
            </div>
          ))}
        </div>

        {/* Mobile (M4): condensed one-line pedigree — `sire × dam · ds damsSire`.
            Both this and the table are always in the markup; CSS media queries
            pick one per breakpoint (no client-side measurement, SSR-safe). */}
        {sire || dam ? (
          <div className={css.pedLine}>
            {sire ? <span className={css.pedLineParent}>{sire}</span> : null}
            {sire && dam ? <span className={css.pedLineSep}> × </span> : null}
            {dam ? <span className={css.pedLineParent}>{dam}</span> : null}
            {damsSire ? (
              <>
                <span className={css.pedLineSep}> · ds </span>
                <span className={css.pedLineDs}>{damsSire}</span>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ListingCard;
